import IntelligentContentParser from './IntelligentContentParser';

const STORAGE_PREFIX = 'smartPasteAssociationMemory';
const MAX_STORED_ASSOCIATIONS = 200;
const ASSOCIATION_STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'bonus',
  'can',
  'effect',
  'extra',
  'gain',
  'grants',
  'have',
  'item',
  'it',
  'of',
  'on',
  'or',
  'per',
  'property',
  'rider',
  'the',
  'this',
  'to',
  'weapon',
  'while',
  'with',
  'you',
  'your'
]);

const normalizeName = (value) => IntelligentContentParser.normalizeParsedEntryName(value);
const buildParentFingerprint = (parent) => (
  parent && typeof parent === 'object'
    ? IntelligentContentParser.buildParsedEntryFingerprint(
        parent,
        parent?.sourceSection || parent?.parserMeta?.sourceSection || parent?.type || 'equipment'
      )
    : ''
);
const extractAssociationTopic = (rider) => (
  normalizeName(rider?.name || '')
    .split(' ')
    .filter((token) => token && !ASSOCIATION_STOPWORDS.has(token))
    .slice(0, 3)
    .join(' ')
);
const normalizeAvailableParents = (availableParents = []) => (
  (Array.isArray(availableParents) ? availableParents : []).map((parent) => {
    if (typeof parent === 'string') {
      return {
        name: parent,
        normalizedName: normalizeName(parent),
        fingerprint: '',
        sourceSection: '',
        lineStart: null,
        type: ''
      };
    }
    if (!parent || typeof parent !== 'object') return null;
    return {
      ...parent,
      normalizedName: normalizeName(parent.name || ''),
      fingerprint: buildParentFingerprint(parent),
      sourceSection: normalizeName(parent?.parserMeta?.sourceSection || parent?.sourceSection || ''),
      lineStart: Number.isInteger(parent?.parserMeta?.lineStart) ? parent.parserMeta.lineStart : null,
      type: normalizeName(parent?.type || '')
    };
  }).filter((parent) => parent?.normalizedName)
);

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const buildAssociationStorageKey = (characterId) => (
  `${STORAGE_PREFIX}:${String(characterId || 'global')}`
);

export const buildAssociationKeys = (associationKind, rider) => {
  if (!associationKind || !rider || typeof rider !== 'object') {
    return { exactKey: null, nameKey: null, contextKey: null };
  }

  const fingerprint = IntelligentContentParser.buildParsedEntryFingerprint(rider, associationKind);
  const normalizedName = normalizeName(rider?.name || '');
  const normalizedType = normalizeName(rider?.type || '');
  const normalizedSection = normalizeName(rider?.parserMeta?.sourceSection || '');
  const associationTopic = extractAssociationTopic(rider);

  return {
    exactKey: fingerprint ? `exact|${associationKind}|${fingerprint}` : null,
    nameKey: normalizedName ? `name|${associationKind}|${normalizedName}` : null,
    contextKey: [associationKind, normalizedType, normalizedSection, associationTopic].some(Boolean)
      ? `context|${[associationKind, normalizedType, normalizedSection, associationTopic].filter(Boolean).join('|')}`
      : null
  };
};

const pruneAssociationPreferences = (preferences) => {
  const entries = Object.entries(preferences || {});
  if (entries.length <= MAX_STORED_ASSOCIATIONS) return preferences || {};

  return entries
    .sort((left, right) => {
      const leftTime = Date.parse(left[1]?.updatedAt || '') || 0;
      const rightTime = Date.parse(right[1]?.updatedAt || '') || 0;
      return rightTime - leftTime;
    })
    .slice(0, MAX_STORED_ASSOCIATIONS)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
};

export const getRememberedAssociation = (preferences, associationKind, rider, availableParentNames = []) => {
  if (!preferences || typeof preferences !== 'object') return null;

  const { exactKey, nameKey, contextKey } = buildAssociationKeys(associationKind, rider);
  const availableParents = normalizeAvailableParents(availableParentNames);
  const parentsByFingerprint = new Map(
    availableParents
      .filter((parent) => parent.fingerprint)
      .map((parent) => [parent.fingerprint, parent])
  );
  const parentsByName = new Map(
    availableParents.map((parent) => [parent.normalizedName, parent])
  );
  const candidate = (
    (exactKey && preferences[exactKey]) ||
    (nameKey && preferences[nameKey]) ||
    (contextKey && preferences[contextKey]) ||
    null
  );
  if (!candidate || typeof candidate !== 'object') return null;

  if (candidate.mode === 'attach' && candidate.parentName) {
    if (candidate.parentFingerprint && parentsByFingerprint.has(candidate.parentFingerprint)) {
      const parent = parentsByFingerprint.get(candidate.parentFingerprint);
      return {
        ...candidate,
        parentName: parent?.name || candidate.parentName,
        parentFingerprint: parent?.fingerprint || candidate.parentFingerprint
      };
    }

    const parent = parentsByName.get(normalizeName(candidate.parentName));
    if (!parent) return null;
    return {
      ...candidate,
      parentName: parent.name,
      parentFingerprint: parent.fingerprint || candidate.parentFingerprint || ''
    };
  }

  return candidate;
};

export const recordAssociation = (preferences, { associationKind, rider, mode, parentName, parentFingerprint } = {}) => {
  if (!associationKind || !rider || !mode) return preferences || {};

  const { exactKey, nameKey, contextKey } = buildAssociationKeys(associationKind, rider);
  const next = { ...(preferences || {}) };
  const payload = {
    mode,
    parentName: parentName || '',
    parentFingerprint: parentFingerprint || '',
    updatedAt: new Date().toISOString()
  };

  if (exactKey) next[exactKey] = payload;
  if (nameKey) next[nameKey] = payload;
  if (contextKey) next[contextKey] = payload;

  return pruneAssociationPreferences(next);
};

export const recordAssociations = (preferences, decisions = []) => (
  (Array.isArray(decisions) ? decisions : []).reduce(
    (acc, decision) => recordAssociation(acc, decision),
    preferences || {}
  )
);

export const loadAssociationStore = (characterId) => {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(buildAssociationStorageKey(characterId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const saveAssociationStore = (characterId, preferences) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(
      buildAssociationStorageKey(characterId),
      JSON.stringify(pruneAssociationPreferences(preferences || {}))
    );
  } catch (error) {
    // Ignore storage quota / availability issues; SmartPaste still works without persistence.
  }
};

const SmartPasteAssociationMemory = {
  buildAssociationStorageKey,
  buildAssociationKeys,
  getRememberedAssociation,
  recordAssociation,
  recordAssociations,
  loadAssociationStore,
  saveAssociationStore
};

export default SmartPasteAssociationMemory;
